/* DER Package Protection toggle.
 *
 * Adds or removes the single Package Protection line. The cart is the state:
 * the line is there or it is not. After every change the theme's own cart
 * sections are re-rendered from Shopify's Section Rendering API, so the fee
 * shown is always the one Liquid computed from the live cart, and the Cart
 * Transform function prices the line itself server-side.
 *
 * Fail-safe by construction. Every network call is wrapped; on any error the
 * checkbox reverts, a one-line message shows, and the cart keeps working.
 * This file never touches quantities, prices, shipping or discounts of any
 * other line.
 */
(function () {
  'use strict';

  if (customElements.get('der-package-protection')) return;

  var PROP_KEY = '_der_package_protection';
  // Visible line property, same wording Navidium writes on orders today, so
  // the team and the customer read the line the way they already do.
  var DISCLAIMER_KEY = 'Disclaimer';
  var DISCLAIMER_TEXT = 'This is an optional additional service for expedited replacements and is not a shipping fee.';
  var ATTR_KEY = 'package_protection';
  var QA_STORAGE = 'der_pp_qa';

  /* ---- QA gate: same pattern as the cart upsell engine ---------------- */
  function qaOptedIn() {
    try {
      var params = new URLSearchParams(window.location.search);
      var flag = params.get('der_pp_qa');
      if (flag === '1') { localStorage.setItem(QA_STORAGE, '1'); return true; }
      if (flag === '0') { localStorage.removeItem(QA_STORAGE); return false; }
      return localStorage.getItem(QA_STORAGE) === '1';
    } catch (e) {
      return false;
    }
  }

  function applyQaGate(root) {
    var scope = root || document;
    var gated = scope.querySelectorAll('.der-pp-qa');
    if (!gated.length) return;
    var show = qaOptedIn();
    gated.forEach(function (el) { el.hidden = !show; });
  }

  /* ---- Section rendering helpers -------------------------------------- */
  function sectionsToRequest() {
    var names = ['cart-icon-bubble', 'free-shipping-notice'];
    var drawer = document.getElementById('cart-drawer');
    if (drawer && drawer.closest('.shopify-section')) {
      names.push(drawer.closest('.shopify-section').id.replace('shopify-section-', ''));
    }
    var summary = document.getElementById('cart-summary');
    if (summary && summary.dataset.section) names.push(summary.dataset.section);
    var items = document.getElementById('cart-items');
    if (items && items.dataset.section) names.push(items.dataset.section);
    return Array.from(new Set(names));
  }

  function swap(data, id, sectionName, selector) {
    var host = document.getElementById(id);
    var html = data.sections && data.sections[sectionName];
    if (!host || !html) return;
    var tmpl = document.createElement('template');
    tmpl.innerHTML = html;
    var source = tmpl.content.querySelector(selector);
    var dest = host.matches(selector) ? host : (host.querySelector(selector) || host);
    if (source) dest.innerHTML = source.innerHTML;
  }

  function render(data) {
    if (!data || !data.sections) return;
    var drawer = document.getElementById('cart-drawer');
    var drawerSection = drawer && drawer.closest('.shopify-section')
      ? drawer.closest('.shopify-section').id.replace('shopify-section-', '')
      : null;
    var items = document.getElementById('cart-items');
    var itemsSection = items ? items.dataset.section : null;
    var summary = document.getElementById('cart-summary');

    if (itemsSection) {
      swap(data, 'cart-items', itemsSection, 'cart-items');
      swap(data, 'der-su-featured', itemsSection, '#der-su-featured');
    }
    if (drawerSection) {
      swap(data, 'cart-items', drawerSection, 'cart-items');
      swap(data, 'der-su-featured', drawerSection, '#der-su-featured');
      swap(data, 'cart-drawer', drawerSection, '.cart-drawer__summary');
    }
    if (summary && summary.dataset.section) swap(data, 'cart-summary', summary.dataset.section, '.cart__summary');
    swap(data, 'free-shipping-notice', 'free-shipping-notice', '.free-shipping-notice');

    var bubble = document.getElementById('cart-icon-bubble');
    if (bubble && data.sections['cart-icon-bubble']) {
      var tmpl = document.createElement('template');
      tmpl.innerHTML = data.sections['cart-icon-bubble'];
      var source = tmpl.content.querySelector('.shopify-section');
      if (source) bubble.innerHTML = source.innerHTML;
    }

    applyQaGate();
    document.dispatchEvent(new CustomEvent('on:cart:updated', { bubbles: true }));
  }

  async function post(route, body) {
    var response = await fetch(route, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(Object.assign({}, body, {
        sections: sectionsToRequest(),
        sections_url: window.location.pathname
      }))
    });
    var data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message || String(response.status));
    return data;
  }

  /* ---- Cart inspection ------------------------------------------------ */
  function isProtectionItem(item, productId) {
    if (!item) return false;
    if (productId && Number(item.product_id) === Number(productId)) return true;
    var type = (item.product_type || '').trim().toLowerCase();
    return type === 'package protection';
  }

  /* Protection must never be the only thing in the cart, and never more than
   * one unit on one line. Returns true if it changed the cart. */
  async function tidy(cart, productId) {
    if (!cart || !Array.isArray(cart.items)) return false;
    var protection = [];
    var merchandise = 0;
    cart.items.forEach(function (item, i) {
      if (isProtectionItem(item, productId)) protection.push({ line: i + 1, qty: item.quantity });
      else merchandise += item.quantity;
    });
    if (!protection.length) return false;

    if (merchandise === 0) {
      // Remove from the highest line down so indexes stay valid.
      for (var i = protection.length - 1; i >= 0; i--) {
        await post(window.theme && window.theme.routes ? window.theme.routes.cartChange : '/cart/change.js', { line: protection[i].line, quantity: 0 });
      }
      return true;
    }
    var changed = false;
    if (protection[0].qty !== 1) {
      await post(window.theme && window.theme.routes ? window.theme.routes.cartChange : '/cart/change.js', { line: protection[0].line, quantity: 1 });
      changed = true;
    }
    for (var j = protection.length - 1; j >= 1; j--) {
      await post(window.theme && window.theme.routes ? window.theme.routes.cartChange : '/cart/change.js', { line: protection[j].line, quantity: 0 });
      changed = true;
    }
    return changed;
  }

  /* ---- The element ---------------------------------------------------- */
  class DerPackageProtection extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('.der-pp__input');
      this.errorEl = this.querySelector('.der-pp__error');
      if (!this.input) return;
      this.onChange = this.handleChange.bind(this);
      this.input.addEventListener('change', this.onChange);
      applyQaGate(this.parentElement || document);
    }

    disconnectedCallback() {
      if (this.input && this.onChange) this.input.removeEventListener('change', this.onChange);
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    async handleChange() {
      var wantOn = this.input.checked;
      var variantId = Number(this.dataset.variantId);
      var productId = Number(this.dataset.productId);
      var changeRoute = window.theme && window.theme.routes ? window.theme.routes.cartChange : '/cart/change.js';
      if (!variantId) return;

      this.classList.add('is-busy');
      if (this.errorEl) this.errorEl.hidden = true;

      try {
        var data;
        if (wantOn) {
          // Never add a second line: re-read the cart first.
          var current = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
          var already = (current.items || []).some(function (it) { return isProtectionItem(it, productId); });
          if (!already) {
            var props = {};
            props[PROP_KEY] = 'true';
            props[DISCLAIMER_KEY] = DISCLAIMER_TEXT;
            await post('/cart/add.js', { items: [{ id: variantId, quantity: 1, properties: props }] });
          }
          var attrsOn = {};
          attrsOn[ATTR_KEY] = 'true';
          data = await post('/cart/update.js', { attributes: attrsOn });
        } else {
          var cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
          var lines = [];
          (cart.items || []).forEach(function (it, i) { if (isProtectionItem(it, productId)) lines.push(i + 1); });
          for (var i = lines.length - 1; i >= 0; i--) {
            await post(changeRoute, { line: lines[i], quantity: 0 });
          }
          var attrsOff = {};
          attrsOff[ATTR_KEY] = 'false';
          data = await post('/cart/update.js', { attributes: attrsOff });
        }

        if (await tidy(data, productId)) {
          data = await post('/cart/update.js', {});
        }
        render(data);
      } catch (error) {
        this.input.checked = !wantOn;
        this.showError('Sorry, that did not go through. Your cart is unchanged.');
        try { console.log('[der-pp]', error); } catch (e) { /* noop */ }
      } finally {
        this.classList.remove('is-busy');
      }
    }
  }

  customElements.define('der-package-protection', DerPackageProtection);

  /* Keep the cart tidy after OTHER changes (a merchandise line removed by
   * the theme's own controls, a quantity change from the upsell engine). */
  var tidying = false;
  async function tidyFromLiveCart() {
    if (tidying) return;
    var el = document.querySelector('der-package-protection');
    var productId = el ? Number(el.dataset.productId) : null;
    tidying = true;
    try {
      var cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
      var protection = (cart.items || []).filter(function (it) { return isProtectionItem(it, productId); });
      if (!protection.length) return;
      var merchandise = (cart.items || []).filter(function (it) { return !isProtectionItem(it, productId); })
        .reduce(function (n, it) { return n + it.quantity; }, 0);
      var needs = merchandise === 0 || protection.length > 1 || protection[0].quantity !== 1;
      if (!needs) return;
      if (await tidy(cart, productId)) {
        var data = await post('/cart/update.js', {});
        render(data);
      }
    } catch (e) {
      /* Best effort only. Never block. */
    } finally {
      tidying = false;
    }
  }

  document.addEventListener('on:line-item:change', function () { setTimeout(tidyFromLiveCart, 50); });
  document.addEventListener('on:cart:updated', function () { setTimeout(tidyFromLiveCart, 50); });
  document.addEventListener('DOMContentLoaded', function () { applyQaGate(); setTimeout(tidyFromLiveCart, 300); });
  if (document.readyState !== 'loading') { applyQaGate(); setTimeout(tidyFromLiveCart, 300); }
})();
